// v1.9.88 — printHtml router, ported from Amahadari's utils/printHtml.js.
// Renders a complete HTML document and routes to the right print path:
//
//   1. Desktop / Electron — hidden iframe + iframe.contentWindow.print().
//      Native print dialog, no popup, no browser chrome.
//   2. Mobile browser — generate a PDF via html2pdf and trigger download.
//      User picks Open / Share / Print from the download notification.
//   3. Capacitor APK — generate PDF, write via @capacitor/filesystem, open
//      the OS share sheet via @capacitor/share. User saves, opens, or
//      sends from there.
//
// The Mobile Print Mode toggle in System Settings overrides path 2 with
// path 1's print-dialog behaviour for phones that prefer the native dialog.

import { isMobileApp } from './platform';

const PHONE_BREAKPOINT = 768;
export const MOBILE_PRINT_MODE_KEY = 'church.mobilePrintMode';

const isPhone = () =>
  typeof window !== 'undefined' && window.innerWidth <= PHONE_BREAKPOINT;

const readMobilePrintMode = () => {
  try {
    const v = window.localStorage.getItem(MOBILE_PRINT_MODE_KEY);
    return v === 'print' ? 'print' : 'pdf';
  } catch (_) {
    return 'pdf';
  }
};

const filenameFromHtml = (html) => {
  try {
    const m = html.match(/<title[^>]*>([^<]+)<\/title>/i);
    const raw = (m && m[1]) || 'statement';
    return raw.replace(/[^a-z0-9._-]+/gi, '_').slice(0, 60) || 'statement';
  } catch (_) {
    return 'statement';
  }
};

function renderInIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;top:0;left:-10000px;width:210mm;height:auto;min-height:297mm;border:0;background:#fff;';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  let removed = false;
  const cleanup = () => {
    if (removed) return;
    removed = true;
    try { iframe.remove(); } catch (_) {}
  };

  return { iframe, doc, cleanup };
}

async function buildPdfBlob(html) {
  const html2pdf = (await import('html2pdf.js')).default;
  const { iframe, doc, cleanup } = renderInIframe(html);
  await new Promise((r) => setTimeout(r, 250));
  const body = doc.body;
  const heightPx = Math.max(body.scrollHeight || 0, 200);
  // A4 width 210mm; estimate rendered height from the body and cap for safety.
  const heightMm = Math.min(Math.max(heightPx / 3.7795, 100), 5000);
  try {
    const pdf = await html2pdf()
      .set({
        margin: 0,
        filename: `${filenameFromHtml(html)}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          backgroundColor: '#ffffff',
          windowWidth: body.scrollWidth,
          windowHeight: body.scrollHeight,
        },
        jsPDF: { unit: 'mm', format: [210, heightMm], orientation: 'portrait' },
      })
      .from(body)
      .outputPdf('blob');
    return pdf;
  } finally {
    cleanup();
  }
}

export const shouldShowMobilePdfButton = () =>
  isPhone() && readMobilePrintMode() === 'pdf';

// v1.9.90 — exported for callers that still build their own print HTML and
// just need the "open window + print" behaviour. New code
// should prefer the default printHtml() export instead; this is for the
// 6 existing print flows that aren't trivially convertible yet.
export function openPreview(html) {
  openInNewWindow(html);
}

const isCapacitorBridgeReachable = () =>
  typeof window !== 'undefined' &&
  typeof window.Capacitor !== 'undefined' &&
  typeof window.Capacitor.isPluginAvailable === 'function' &&
  window.Capacitor.isPluginAvailable('Filesystem') &&
  window.Capacitor.isPluginAvailable('Share');

export async function downloadPdf(html) {
  try {
    if (isCapacitorBridgeReachable()) {
      await sharePdfFromApk(html);
    } else {
      await downloadPdfInBrowser(html);
    }
  } catch (err) {
    try {
      await downloadPdfInBrowser(html);
      return;
    } catch (_) {}
    alert(`PDF failed: ${err?.message || err}`);
    throw err;
  }
}

async function downloadPdfInBrowser(html) {
  const blob = await buildPdfBlob(html);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameFromHtml(html)}.pdf`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    try { a.remove(); } catch (_) {}
    URL.revokeObjectURL(url);
  }, 1000);
}

async function sharePdfFromApk(html) {
  const blob = await buildPdfBlob(html);
  const base64 = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => {
      const r = fr.result || '';
      const i = String(r).indexOf(',');
      resolve(i >= 0 ? String(r).slice(i + 1) : String(r));
    };
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(blob);
  });

  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { Share } = await import('@capacitor/share');

  const filename = `${filenameFromHtml(html)}.pdf`;
  const written = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });

  try {
    await Share.share({
      title: filename,
      url: written.uri,
      dialogTitle: 'Share statement',
    });
  } catch (_) {
    // User dismissed the share sheet — file is still saved at written.uri.
  }
}

function printViaIframe(html) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  let removed = false;
  const cleanup = () => {
    if (removed) return;
    removed = true;
    try { iframe.remove(); } catch (_) {}
  };

  iframe.onload = () => {
    try {
      const cw = iframe.contentWindow;
      cw.focus();
      if (cw && 'onafterprint' in cw) cw.onafterprint = cleanup;
      cw.print();
    } catch (_) {
      cleanup();
    }
    setTimeout(cleanup, 60_000);
  };

  if ('srcdoc' in iframe) {
    iframe.srcdoc = html;
  } else {
    iframe.onload = () => {
      try {
        const d = iframe.contentDocument || iframe.contentWindow.document;
        d.open(); d.write(html); d.close();
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (_) {}
      setTimeout(cleanup, 60_000);
    };
    iframe.src = 'about:blank';
  }
}

// v1.9.116 — open the print view in a new window and start printing. The
// blue "Preview / Print / Back" bar is gone, and Print buttons are hidden on
// phones / the APK (useIsMobile), so this is a desktop path. If pop-ups are
// blocked we ask the user to allow them instead of overwriting the current
// page (which, without the bar, would leave them stuck on the print view).
function openInNewWindow(html) {
  const w = window.open('', '_blank');
  if (!w) {
    alert('Please allow pop-ups to print');
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 400);
}

export default function printHtml(html) {
  if (typeof document === 'undefined') return;

  if (isCapacitorBridgeReachable()) {
    sharePdfFromApk(html).catch(() => downloadPdfInBrowser(html).catch(() => openInNewWindow(html)));
    return;
  }

  // Phone or APK — new-window path works in both the thin-shell WebView and
  // mobile Chrome / Safari, where <a download> is unreliable.
  if (isPhone() || isMobileApp()) {
    openInNewWindow(html);
    return;
  }

  printViaIframe(html);
}
